import { computed, Injectable, ResourceRef } from '@angular/core';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
  isCompositeFilterDescriptor,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import { DefaultPageSize } from '@unity/utils/constants';
import { GridifyQuery, Paging, UnityDto } from '@unity/utils/types';
import { ConditionalOperator, GridifyQueryBuilder } from 'gridify-client';

export class GridResource<TDto extends Readonly<UnityDto>> {
  #resource: ResourceRef<Paging<TDto> | undefined>;

  public constructor(resource: ResourceRef<Paging<TDto> | undefined>) {
    this.#resource = resource;
  }

  public readonly $data = computed(() => {
    const actualValue = this.#resource.value();
    const isLoading = this.#resource.isLoading();
    const err = this.#resource.error();

    if (!!err || isLoading || !actualValue) {
      return { data: [], total: 0 };
    }

    return { data: actualValue.data, total: actualValue.count };
  });

  public get $loading() {
    return this.#resource.isLoading;
  }

  public readonly $error = computed(() => {
    const err = this.#resource.error();
    return err?.message;
  });
}

@Injectable({
  providedIn: 'root',
})
export class GridService<TDto extends Readonly<UnityDto>> {
  public resourceForGrid(resource: ResourceRef<Paging<TDto> | undefined>) {
    return new GridResource<TDto>(resource);
  }

  public buildQuery(e: State): GridifyQuery {
    const builder = new GridifyQueryBuilder();

    e.skip ??= 0;
    e.take ??= DefaultPageSize;

    const pageNumber = e.skip / e.take + 1;
    builder.setPage(pageNumber);
    builder.setPageSize(e.take);

    if (e.sort) {
      this.addOrderBy(builder, e.sort);
    }

    if (e.filter) {
      this.addFilters(builder, e.filter);
    }

    const query = builder.build();
    if (query.filter === '') {
      delete query.filter;
    }
    if (query.orderBy === '') {
      delete query.orderBy;
    }
    return query;
  }

  public addOrderBy(builder: GridifyQueryBuilder, sortDescriptors: SortDescriptor[]): void {
    sortDescriptors.forEach(sd => {
      const field = this.mapFieldName(sd.field);
      if (sd.dir && sd.dir == 'desc') {
        builder.addOrderBy(field, true);
      } else {
        builder.addOrderBy(field);
      }
    });
  }

  public addFilters(builder: GridifyQueryBuilder, filter: CompositeFilterDescriptor): void {
    const operator: CompositeFilterDescriptor['logic'] = filter.logic;
    filter.filters.forEach((filter: CompositeFilterDescriptor | FilterDescriptor, i: number) => {
      if (i > 0) {
        this.addLogicalOperator(builder, operator);
      }
      if (isCompositeFilterDescriptor(filter)) {
        builder.startGroup();
        this.addFilters(builder, filter);
        builder.endGroup();
      } else {
        this.addCondition(builder, filter);
      }
    });
  }

  private addCondition(builder: GridifyQueryBuilder, filter: FilterDescriptor): void {
    if (filter.field === undefined || typeof filter.field === 'function') {
      // filter.field will only be a function if a custom FilterDescriptor is defined that requires one. We don't use this anywhere.
      return;
    }

    // Gridify uses the "null" keyword in filtering operations, see https://alirezanet.github.io/Gridify/guide/gridifyGlobalConfiguration#allownullsearch
    if (filter.operator === FilterOperator.IsNull || filter.operator === FilterOperator.IsEmpty) {
      builder.addCondition(this.mapFieldName(filter.field), ConditionalOperator.Equal, 'null');
    } else if (filter.operator === FilterOperator.IsNotNull || filter.operator === FilterOperator.IsNotEmpty) {
      builder.addCondition(this.mapFieldName(filter.field), ConditionalOperator.NotEqual, 'null');
    } else {
      const gridifyOperator: ConditionalOperator = this.getConditionalOperator(filter.operator as FilterOperator);

      let value = filter.value;
      // Date.toString() includes the local timezone in parenthesis, which breaks Gridify.
      if (value instanceof Date) {
        value = value.toDateString();
      }

      builder.addCondition(this.mapFieldName(filter.field), gridifyOperator, value);
    }
  }

  private addLogicalOperator(builder: GridifyQueryBuilder, logic: CompositeFilterDescriptor['logic']) {
    if (logic === 'and') {
      builder.and();
    } else {
      builder.or();
    }
  }

  private mapFieldName(name: string): string {
    // Case insensitive filtering is currently turned on (check GridifyGlobalConfiguration.CaseInsensitiveFiltering), so no need to map to CamelCase, just remove underscores.
    // See https://alirezanet.github.io/Gridify/guide/gridifyGlobalConfiguration#caseinsensitivefiltering
    const re = /[_]/g;
    return name.replaceAll(re, '');
  }

  private getConditionalOperator(operator: FilterOperator) {
    switch (operator) {
      case FilterOperator.Contains:
        return ConditionalOperator.Contains;
      case FilterOperator.DoesNotContain:
        return ConditionalOperator.NotContains;
      case FilterOperator.DoesNotEndWith:
        return ConditionalOperator.NotEndsWith;
      case FilterOperator.DoesNotStartWith:
        return ConditionalOperator.NotStartsWith;
      case FilterOperator.EndsWith:
        return ConditionalOperator.EndsWith;
      case FilterOperator.EqualTo:
        return ConditionalOperator.Equal;
      case FilterOperator.GreaterThan:
        return ConditionalOperator.GreaterThan;
      case FilterOperator.GreaterThanOrEqual:
        return ConditionalOperator.GreaterThanOrEqual;
      case FilterOperator.LessThan:
        return ConditionalOperator.LessThan;
      case FilterOperator.LessThanOrEqual:
        return ConditionalOperator.LessThanOrEqual;
      case FilterOperator.NotEqualTo:
        return ConditionalOperator.NotEqual;
      case FilterOperator.StartsWith:
        return ConditionalOperator.StartsWith;
      default:
        return ConditionalOperator.Contains;
    }
  }
}
